import { type ISourceOptions, MoveDirection, OutMode } from '@tsparticles/engine';

export type ParticlePalette = 'accent' | 'constellation';

/** True unless the visitor asked for reduced motion. */
export function isParticlesEnabled(): boolean {
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  return !(mediaQuery && mediaQuery.matches);
}

// Cache the options object per resolved config so the `options` prop passed to
// `<Particles>` gets a STABLE reference. Returning a fresh object on every render
// makes the component tear down and reload the container each cycle, which
// snowballs into a runaway loop that exhausts memory.
const optionsCache = new Map<string, ISourceOptions>();

const DISABLED_OPTIONS: ISourceOptions = { particles: { number: { value: 0 } } };

export function resolveParticleOptions(
  isDark: boolean,
  palette: ParticlePalette = 'accent',
  enabled: boolean = isParticlesEnabled(),
): ISourceOptions {
  const key = `${enabled ? 'on' : 'off'}:${isDark ? 'dark' : 'light'}:${palette}`;
  let options = optionsCache.get(key);
  if (!options) {
    options = enabled
      ? palette === 'constellation'
        ? buildConstellationOptions(isDark)
        : buildAccentOptions(isDark)
      : DISABLED_OPTIONS;
    optionsCache.set(key, options);
  }
  return options;
}

/**
 * Animated palette that mirrors the `.site-title` / navbar accent ramp: the
 * particle hue sweeps the same band over ~8s, then wraps back to the start,
 * matching the flow of the title gradient.
 *   dark  -> teal -> sky -> violet -> pink        (bright pastels,  hue 166->330)
 *   light -> teal -> deep sky -> indigo -> fuchsia (vivid jewel,    hue 184->300)
 * Ported verbatim from the Angular `ParticlesService.buildOptions`.
 */
function buildAccentOptions(isDark: boolean): ISourceOptions {
  const hueStart = isDark ? 166 : 184;
  const hueEnd = isDark ? 330 : 300;
  const baseColor = isDark ? 'hsl(166, 76%, 66%)' : 'hsl(184, 62%, 33%)';
  const hueAnimation = {
    enable: true,
    speed: isDark ? 34 : 26,
    sync: true,
    min: hueStart,
    max: hueEnd,
  };
  const linkColor = 'mid';
  const accent = isDark ? '#f0fdfa' : '#4338ca';

  return {
    background: { color: { value: 'transparent' } },
    fpsLimit: 60,
    interactivity: {
      events: {
        onClick: { enable: true, mode: 'push' },
        onHover: {
          enable: true,
          mode: ['grab', 'bubble'],
          parallax: { enable: true, force: 40, smooth: 20 },
        },
        resize: { enable: true, delay: 0, factor: 1 },
      },
      modes: {
        grab: {
          distance: 220,
          links: { opacity: isDark ? 0.9 : 0.7, color: accent },
        },
        bubble: { distance: 200, size: 6, duration: 2, opacity: 0.9 },
        push: { quantity: 4 },
        repulse: { distance: 200, duration: 0.4 },
      },
    },
    particles: {
      paint: {
        color: { value: baseColor },
        fill: {
          enable: true,
          color: { value: baseColor, animation: { h: hueAnimation } },
        },
      },
      links: {
        color: linkColor,
        distance: 160,
        enable: true,
        opacity: isDark ? 0.45 : 0.6,
        width: isDark ? 1 : 1.4,
        triangles: { enable: true, color: linkColor, opacity: isDark ? 0.04 : 0.06 },
        shadow: { enable: true, blur: 4, color: accent },
      },
      collisions: { enable: true },
      move: {
        direction: MoveDirection.none,
        enable: true,
        outModes: { default: OutMode.bounce },
        random: true,
        speed: { min: 0.4, max: 1.8 },
        straight: false,
      },
      number: { density: { enable: true }, value: 90 },
      opacity: {
        value: isDark ? { min: 0.15, max: 0.55 } : { min: 0.4, max: 0.85 },
        animation: {
          enable: true,
          speed: 0.6,
          sync: false,
          startValue: 'random',
          mode: 'auto',
          destroy: 'none',
        },
      },
      shape: { type: 'circle' },
      size: {
        value: isDark ? { min: 1, max: 4 } : { min: 1.5, max: 5 },
        animation: {
          enable: true,
          speed: 3,
          sync: false,
          startValue: 'random',
          mode: 'auto',
          destroy: 'none',
        },
      },
      shadow: { enable: true, blur: isDark ? 8 : 4, color: accent },
    },
    detectRetina: true,
    fullScreen: { enable: true, zIndex: -1 },
  };
}

/**
 * Green "constellation" mesh used on the 404 and testing pages. Ported from the
 * legacy `ParticlesJS` (light = deep greens on the white field, dark = bright
 * greens on #212529).
 */
function buildConstellationOptions(isDark: boolean): ISourceOptions {
  const particleColors = isDark
    ? ['#00a651', '#00e676', '#8ff0b8', '#e0f8ec']
    : ['#047857', '#059669', '#10b981'];
  const linkColor = isDark ? '#00e676' : '#059669';
  const accent = isDark ? '#e0f8ec' : '#065f46';
  const glowBlur = isDark ? 8 : 4;

  return {
    autoPlay: true,
    fpsLimit: 60,
    interactivity: {
      detectsOn: 'window',
      events: {
        onClick: { enable: true, mode: 'push' },
        onHover: {
          enable: true,
          mode: ['grab', 'bubble'],
          parallax: { enable: true, force: 40, smooth: 20 },
        },
        resize: { enable: true, delay: 0, factor: 1 },
      },
      modes: {
        grab: { distance: 220, links: { opacity: 0.9, color: accent } },
        bubble: { distance: 200, duration: 2, opacity: 0.9, size: 6 },
        push: { quantity: 4 },
        repulse: { distance: 200, duration: 0.4 },
      },
    },
    particles: {
      color: { value: particleColors },
      paint: { color: { value: particleColors } },
      links: {
        color: linkColor,
        distance: 160,
        enable: true,
        opacity: 0.45,
        width: 1,
        triangles: { enable: true, color: linkColor, opacity: 0.04 },
        shadow: { enable: true, blur: 4, color: accent },
      },
      collisions: { enable: true },
      move: {
        direction: MoveDirection.none,
        enable: true,
        outModes: { default: OutMode.bounce },
        random: true,
        speed: { min: 0.4, max: 1.8 },
        straight: false,
      },
      number: { density: { enable: true }, value: 90 },
      opacity: {
        value: { min: 0.15, max: 0.55 },
        animation: {
          enable: true,
          speed: 0.6,
          sync: false,
          startValue: 'random',
          mode: 'auto',
          destroy: 'none',
        },
      },
      shape: { type: 'circle' },
      size: {
        value: { min: 1, max: 4 },
        animation: {
          enable: true,
          speed: 3,
          sync: false,
          startValue: 'random',
          mode: 'auto',
          destroy: 'none',
        },
      },
      shadow: { enable: true, blur: glowBlur, color: accent },
    },
    detectRetina: true,
    fullScreen: { enable: true, zIndex: -1 },
    pauseOnBlur: true,
    pauseOnOutsideViewport: true,
  };
}
